import { escapeHtml } from './dom.js';

/**
 * A yes/no question, asked in this app's own voice.
 *
 * `window.confirm` was what asked before, and it was conspicuous: the
 * browser's dialog carries the page's ORIGIN, its own typeface and its own
 * buttons, so a question about unsaved changes arrived looking like a
 * security warning from somewhere else. It is also unlabelled — "OK" and
 * "Cancel" are the only two words it can ever say, which leaves the reader
 * to work out which of them saves.
 *
 * A native `<dialog>` rather than a Bootstrap modal, because every one of
 * these is asked from INSIDE a modal: the transaction forms are modals, and
 * stacking a second Bootstrap modal on the first means two backdrops, two
 * scroll locks and a focus trap each. `showModal()` puts the dialog in the
 * browser's top layer, which is above everything on the page by definition
 * and needs none of that — and it brings Escape, the backdrop, the focus
 * trap and inertness of the page behind for nothing.
 */
export interface ConfirmOptions {
  /** The question, in a few words. */
  title: string;
  /**
   * What happens either way — the part `window.confirm` had nowhere to put.
   *
   * Paragraphs, either as a list or as one string with blank lines between
   * them. These questions are mostly about deletions, and what each of them
   * has to say is three separate things: what goes, what does NOT go, and
   * how to undo it. Run together they are a wall nobody reads.
   */
  message?: string | readonly string[];
  /** The affirmative button's label. Says what it DOES: never "OK". */
  confirm: string;
  cancel?: string;
  /**
   * `danger` for an answer that destroys something: it colours the button
   * and moves the opening focus onto Cancel, so Enter on a dialog that
   * appeared under someone's fingers does the harmless thing.
   */
  tone?: 'primary' | 'danger';
}

export function askConfirm(opts: ConfirmOptions): Promise<boolean> {
  const dialog = document.createElement('dialog');

  /*
   * No `showModal` means no top layer, and a dialog that renders behind the
   * form it was asked from is worse than the browser's own. Older engines
   * and non-browser hosts both land here.
   */
  const paragraphs = messageParagraphs(opts.message);
  if (typeof dialog.showModal !== 'function') {
    return Promise.resolve(window.confirm([opts.title, ...paragraphs].join('\n\n')));
  }

  const danger = opts.tone === 'danger';
  dialog.className = 'wl-dlg wl-confirm';
  dialog.innerHTML = `
    <form method="dialog">
      <h2 class="h6 mb-2">${escapeHtml(opts.title)}</h2>
      ${paragraphs.map((p, i) => `<p class="small text-muted mb-${i === paragraphs.length - 1 ? '0' : '2'}">${
        escapeHtml(p)}</p>`).join('')}
      <div class="d-flex justify-content-end gap-2 mt-3">
        <button class="btn btn-sm btn-outline-secondary" value="cancel" data-confirm-no>${
          escapeHtml(opts.cancel ?? 'Cancel')}</button>
        <button class="btn btn-sm ${danger ? 'btn-danger' : 'btn-primary'}" value="confirm" data-confirm-yes>${
          escapeHtml(opts.confirm)}</button>
      </div>
    </form>`;
  document.body.appendChild(dialog);

  return new Promise<boolean>((resolve) => {
    dialog.addEventListener('close', () => {
      // Escape and the backdrop both close with an empty `returnValue`, and
      // both of them mean no. Only the button says yes.
      const answer = dialog.returnValue === 'confirm';
      dialog.remove();
      resolve(answer);
    }, { once: true });
    dialog.showModal();
    /*
     * Where the focus lands is the whole of what Enter does, so it follows
     * the weight of the answer rather than the order of the buttons.
     */
    dialog.querySelector<HTMLButtonElement>(danger ? '[data-confirm-no]' : '[data-confirm-yes]')?.focus();
  });
}

function messageParagraphs(message: ConfirmOptions['message']): string[] {
  if (message === undefined) return [];
  const parts = typeof message === 'string' ? message.split(/\n\s*\n/) : message;
  return parts.map((p) => p.trim()).filter((p) => p !== '');
}
